import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { translate } from "../IntlProviderWrapper";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import { useUpdateOrderComment } from "@/lib/custom-hooks/queryComment";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import ErrorComponent from "../ui/ErrorComponent";

type Props = {
    order_uuid: string;
    show_modal: boolean;
    close_on_save?: boolean;
    onSave?: (order: t.IOrderModelBase) => void;
    onCloseModal: () => void;
};

const EditOrderModal = ({ order_uuid, onCloseModal, show_modal, close_on_save, onSave }: Props) => {
    const [order, setOrder] = useState<t.IOrderModelBase | null>(null);
    const [orderComment, setOrderComment] = useState<string | null>(null);
    const [successful_inserted_comment, setSuccessfulInsertedComment] = useState(false);

    const updateOrderComment = useUpdateOrderComment();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const load = useCallback(async (uuid: string) => {
        try {
            const res = await getBackend2().manufacturing.getOrdersSimple({
                uuids: [uuid],
            });

            if (res.operations.length > 0) {
                const loaded = res.operations[0];
                const comment = loaded.order_comment || "";
                setOrder(loaded);
                setOrderComment(comment);
            }
        } catch (e) {
            console.log((e as Error).message);
        }
    }, []);

    useEffect(() => {
        if (!order_uuid) {
            setOrder(null);
            setOrderComment(null);
            return;
        }
        load(order_uuid);
    }, [order_uuid, load]);

    const onClose = useCallback(() => {
        setSuccessfulInsertedComment(false);
        onCloseModal();
    }, [onCloseModal]);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        if (order) {
            setOrderComment(value);
        }
    };

    const handleSave = async () => {
        if (!order || orderComment == null) {
            return;
        }

        try {
            await updateOrderComment.mutateAsync({ order, comment: orderComment });
            setSuccessfulInsertedComment(true);
            onSave?.({ ...order, order_comment: orderComment });
            if (close_on_save !== false) {
                onClose();
            }
        } catch (e) {
            // Error is handled by the mutation hook rollback.
            console.log((e as Error).message);
        }
    };

    const getComment = (): string => {
        const text = orderComment || "";
        if (order) {
            return text;
        }
        return "";
    };

    return (
        <Modal
            show={show_modal}
            dialogClassName="modal-dialog-scrollable"
            onHide={onClose}
            onEntered={() => textareaRef.current?.focus()}
        >
            <Modal.Header>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    {translate("common.edit", "Edit")}
                    <button type="button" className="close" onClick={onClose}>
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body>
                {successful_inserted_comment && (
                    <ErrorComponent className="mt-0" type="success" msg={translate("common.success_save")} />
                )}
                <label>{translate("common.add_comment", "Add comment")}:</label>
                <textarea
                    ref={textareaRef}
                    className="form-control"
                    style={{ height: 200 }}
                    value={getComment()}
                    name="message"
                    onChange={handleChange}
                />
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                    <span aria-hidden="true">{translate("common.save", "Save")}</span>
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditOrderModal;
